/**
 * One question in the Free PEP Readiness Check.
 *
 * Multiple choice and true or false only, so it stays fast on a phone. Nothing here knows the
 * correct answer: the server grades and never sends it back, so there is no feedback state to
 * render and no way to leak a key through the DOM.
 */
import { QuestionRenderer } from "@/components/QuestionRenderer";
import { LatexText } from "@/components/LatexText";
import { MathRenderer } from "@/components/MathRenderer";
import { subjectStyle } from "./subjects";
import DOMPurify from "dompurify";

export interface PepOption { id: string; text: string; math?: string; imageUrl?: string }
export interface PepQuestionData {
  id: string;
  subject: string;
  topic: string;
  questionType: "multiple-choice" | "true-false";
  questionText: string;
  questionImageUrl?: string;
  questionMath?: string;
  options: PepOption[];
}

interface Props {
  question: PepQuestionData;
  index: number;
  total: number;
  selected: string | null;
  onSelect: (optionId: string) => void;
}

export function PepQuestion({ question, index, total, selected, onSelect }: Props) {
  const style = subjectStyle(question.subject);
  const Icon = style.icon;

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 md:p-5 ${style.card}`}>
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 -rotate-2 ${style.sticker}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.chip}`}>{question.subject}</span>
              <span className="text-xs font-medium text-muted-foreground">{question.topic}</span>
            </div>
            <p className="text-xs font-medium text-muted-foreground mt-1 tabular-nums">
              Question {index + 1} of {total}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <QuestionRenderer
            text={question.questionText}
            imageUrl={question.questionImageUrl}
            mathExpression={question.questionMath}
            className="text-lg md:text-xl font-semibold leading-snug text-foreground"
          />
        </div>
      </div>

      <div className="space-y-2.5" role="radiogroup" aria-label="Answer choices">
        {question.options.map((option, i) => {
          const letter = String.fromCharCode(65 + i);
          const isSelected = selected === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(option.id)}
              data-testid={`pep-option-${letter.toLowerCase()}`}
              className={`w-full text-left rounded-xl border-2 p-3.5 min-h-14 transition-colors ${
                isSelected
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:border-primary/50 hover:bg-primary/5"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`w-8 h-8 rounded-lg flex items-center justify-center border-2 flex-shrink-0 text-sm font-bold ${
                    isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"
                  }`}
                >
                  {letter}
                </span>
                <span className="flex-1 min-w-0 space-y-2 pt-1">
                  {/<[^>]+>/.test(option.text) ? (
                    <span
                      className="block text-base leading-snug prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option.text) }}
                    />
                  ) : (
                    <span className="block text-base leading-snug whitespace-pre-wrap">
                      <LatexText text={option.text} />
                    </span>
                  )}
                  {option.math && (
                    <span className="block py-0.5">
                      <MathRenderer math={option.math} displayMode={false} />
                    </span>
                  )}
                  {option.imageUrl && (
                    <img src={option.imageUrl} alt="" className="max-h-40 w-auto rounded-lg border border-border" />
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
